import classNames from "classnames";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import Link from "@Components/atoms/Link";
import { ReactComponent as IconX } from "@Icons/16x16/x.svg";
import { Upsell, UpsellIdentifiers } from "@Types/upsells";
import { getFormattedTotalPrice, isSelected } from "@Utils/upsells";
import UpsellItem from "./UpsellItem";
import "./style.scss";

interface Props {
  hideUpsells: () => void;
  onSelectUpsell: (selectedUpsells: UpsellIdentifiers) => void;
  onSubmit: () => void;
  onSubmitWithoutUpsell: () => void;
  onUnselectUpsell: (selectedUpsells: UpsellIdentifiers) => void;
  selectedUpsellsTags: UpsellIdentifiers[];
  upsells: Upsell[];
}

const UpsellsModal: FunctionComponent<Props> = ({
  hideUpsells,
  onSelectUpsell,
  onSubmit,
  onSubmitWithoutUpsell,
  onUnselectUpsell,
  selectedUpsellsTags,
  upsells,
}) => {
  const intl = useIntl();
  const [hoveredUpsellTag, setHoveredUpsellTag] = useState<string | null>(null);
  const [selectedImageTag, setSelectedImageTag] = useState<string | null>(upsells[0].tag);

  useEffect(() => {
    if (hoveredUpsellTag) {
      return setSelectedImageTag(hoveredUpsellTag);
    }
    if (selectedUpsellsTags.length > 0) {
      const lastSelectedUpsell = upsells.find((upsell) => {
        return (
          upsell.tag === selectedUpsellsTags[selectedUpsellsTags.length - 1].tag &&
          upsell.value === selectedUpsellsTags[selectedUpsellsTags.length - 1].value
        );
      });
      return lastSelectedUpsell
        ? setSelectedImageTag(lastSelectedUpsell.tag)
        : setSelectedImageTag(upsells[0].tag);
    }
    return setSelectedImageTag(upsells[0].tag);
  }, [hoveredUpsellTag]);

  return (
    <div className="UpsellsModal">
      <div className="UpsellsModal-top">
        <Link icon={<IconX />} iconPosition="left" onClick={() => hideUpsells()} size="s">
          {intl.formatMessage({ id: "cheerz_UpsellsModal_quit" })}
        </Link>
        <h2 className="UpsellsModal-title">
          {intl.formatMessage({ id: "cheerz_UpsellsModal_title" })}
        </h2>
      </div>

      <div className="UpsellsModal-content">
        <div className="UpsellsModal-image-container">
          {upsells.map((upsell) => {
            return (
              <img
                className={classNames("UpsellsModal-image", {
                  "UpsellsModal-image--selected": upsell.tag === selectedImageTag,
                })}
                height={241}
                key={upsell.tag}
                src={upsell.image["1x"]}
                srcSet={`${upsell.image["1x"]} 1x, ${upsell.image["2x"]} 2x, ${upsell.image["3x"]} 3x`}
                width={463}
              />
            );
          })}
        </div>

        <div className="UpsellsModal-form">
          {upsells.map((upsell) => {
            const isUpsellSelected = isSelected(upsell.tag, upsell.value, selectedUpsellsTags);
            return (
              <UpsellItem
                isSelected={isUpsellSelected}
                key={`${upsell.tag}-${upsell.value}`}
                onMouseEnter={() => setHoveredUpsellTag(upsell.tag)}
                onMouseLeave={() => setHoveredUpsellTag(null)}
                onSelectChange={() =>
                  isUpsellSelected ? onUnselectUpsell(upsell) : onSelectUpsell(upsell)
                }
                upsell={upsell}
              />
            );
          })}
        </div>
      </div>
      <div className="UpsellsModal-bottom">
        {selectedUpsellsTags.length > 0 ? (
          <ButtonSmily onClick={() => onSubmit()} size="s" type="primary">
            {intl.formatMessage(
              { id: "cheerz_UpsellsModal_button__with_upsell" },
              { totalPrice: getFormattedTotalPrice(upsells, selectedUpsellsTags, intl) }
            )}
          </ButtonSmily>
        ) : (
          <ButtonSmily onClick={onSubmitWithoutUpsell} size="s" type="primary">
            {intl.formatMessage({ id: "cheerz_UpsellsModal_button__without_upsell" })}
          </ButtonSmily>
        )}
      </div>
    </div>
  );
};

export default UpsellsModal;
